import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRelationshipDocumentCreation } from 'services/repositories/relationships/relationships';
import { addNotification, NotificationType } from 'stores/notifications/notifications';
import { OrderData } from 'support/helpers/orderForms/orderForms';
import { enrichDocument } from 'support/helpers/transformer/transformer';
import { DocumentType, OrderFormDTO } from 'support/types';

import { __fallback__fillSupplierParty } from '../utils/fillSupplierParty';
import { transformOrderToCanonicalFormat } from '../utils/transformOrderToCanonicalFormat';

export const useCreateOrder = (orderFormData: OrderFormDTO | undefined) => {
  const { t } = useTranslation();
  const { mutateAsync, isLoading: isMutating } = useRelationshipDocumentCreation();
  const [isProcessing, setIsProcessing] = useState(false);

  const createOrder = useCallback(
    async (orderData: OrderData) => {
      if (!orderFormData) {
        addNotification(t('common:error.generic'), NotificationType.error);
        throw new Error('Order Forms: No order form data available');
      }

      if (!orderFormData?.data?.relationship_id) {
        addNotification(t('common:error.generic'), NotificationType.error);
        throw new Error('Order Forms: No relationship ID found in order form data');
      }

      try {
        setIsProcessing(true);

        const orderDataWithSupplier = await __fallback__fillSupplierParty(orderData, orderFormData); // TODO: remove when we find the root cause for missing supplier when submitting an order
        const canonical = transformOrderToCanonicalFormat(orderDataWithSupplier, orderFormData);

        const { data: enrichedCanonical } = enrichDocument({
          data: canonical,
          messageType: DocumentType.order,
        });

        return await mutateAsync({
          relationshipId: orderFormData?.data?.relationship_id,
          data: enrichedCanonical,
          application: 'ORDER_FORM',
        });
      } catch (error) {
        addNotification(t('common:error.generic'), NotificationType.error);
        throw error;
      } finally {
        setIsProcessing(false);
      }
    },
    [mutateAsync, orderFormData, t],
  );

  const isLoading = isProcessing || isMutating;
  return { createOrder, isLoading };
};
